import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Combobox, type ComboboxItem } from '@node-space/ui-components'
import { CURRENCIES, PAYMENT_METHOD_OPTIONS } from '../utils'

export interface PaymentLimitsFilterProps {
  currencyFilter: string
  paymentMethodFilter: string
  limitTypeFilter: string
  onCurrencyFilterChange: (value: string) => void
  onPaymentMethodFilterChange: (value: string) => void
  onLimitTypeFilterChange: (value: string) => void
}

export const PaymentLimitsFilters = ({
  currencyFilter,
  paymentMethodFilter,
  limitTypeFilter,
  onCurrencyFilterChange,
  onPaymentMethodFilterChange,
  onLimitTypeFilterChange,
}: PaymentLimitsFilterProps) => {
  const { t } = useTranslation()

  const currencyOptions: ComboboxItem[] = useMemo(
    () =>
      CURRENCIES.map(currency => ({
        value: currency,
        leftText: currency,
        LeftElement: <Avatar name={currency} type="currency" size="small" />,
      })),
    []
  )

  const limitTypeOptions: ComboboxItem[] = useMemo(
    () => [
      { value: 'TRANSACTION', leftText: t('paymentLimits.transactionLimit') },
      { value: 'DAILY', leftText: t('paymentLimits.dailyLimit') },
    ],
    [t]
  )

  return (
    <Box flex gapX={12} gapY={12} className="flex-1">
      <Box className="flex-1 min-w-[150px]">
        <Combobox
          key={`payment-limits-method-filter-${paymentMethodFilter}`}
          id="payment-limits-method-filter"
          testId="payment-limits-method-filter"
          items={PAYMENT_METHOD_OPTIONS}
          defaultValue={paymentMethodFilter}
          selectPlaceholder={t('paymentLimits.paymentMethod')}
          onChange={value => onPaymentMethodFilterChange(value ?? '')}
          onClear={() => onPaymentMethodFilterChange('')}
        />
      </Box>
      <Box className="flex-1 min-w-[150px]">
        <Combobox
          key={`payment-limits-limit-type-filter-${limitTypeFilter}`}
          id="payment-limits-limit-type-filter"
          testId="payment-limits-limit-type-filter"
          items={limitTypeOptions}
          defaultValue={limitTypeFilter}
          selectPlaceholder={t('paymentLimits.limitTypeLabel')}
          onChange={value => onLimitTypeFilterChange(value ?? '')}
          onClear={() => onLimitTypeFilterChange('')}
        />
      </Box>
      <Box className="flex-1 min-w-[150px]">
        <Combobox
          key={`payment-limits-currency-filter-${currencyFilter}`}
          id="payment-limits-currency-filter"
          testId="payment-limits-currency-filter"
          items={currencyOptions}
          defaultValue={currencyFilter}
          selectPlaceholder={t('paymentLimits.currency')}
          onChange={value => onCurrencyFilterChange(value ?? '')}
          onClear={() => onCurrencyFilterChange('')}
        />
      </Box>
    </Box>
  )
}
